import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import {
  VStack,
  InputGroup,
  InputLeftElement,
  Input,
  Box,
  Checkbox,
  Divider,
  WrapItem,
  Wrap,
  Tag,
  HStack,
  Text,
} from "@chakra-ui/react";
import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import { CustomPopover } from "../custom-popover";
import { useFirebaseUser } from "utils/useFirebaseUser";

interface Props {
  value: string[];
  onChange: (newValue: string[]) => void;
}

export function IndustryFilter({ value, onChange }: Props) {
  const [industries, setIndustries] = useState<string[]>([]);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredIndutries, setfilteredIndustries] = useState<string[]>([]);
  const { data: session } = useSession() ?? {};
  const isAuthenticated = useFirebaseUser();

  useEffect(() => {
    if (session && isAuthenticated && !hasFetched) {
      setHasFetched(true);
      fetchIndustries();
    }
  }, [session, isAuthenticated]);

  const fetchIndustries = async () => {
    try {
      const response = await fetch("/api/database/getIndustries");
      const data = await response.json();

      if (data.success) {
        setIndustries(data.industries);
        setfilteredIndustries(data.industries);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!industries || industries.length === 0) {
      return;
    }

    if (!searchValue) {
      setfilteredIndustries([...industries]);
    } else {
      setfilteredIndustries(
        industries.filter((i) =>
          i.toLowerCase().includes(searchValue.toLowerCase())
        )
      );
    }
  }, [searchValue]);

  return (
    <CustomPopover
      property="Industry"
      value={value?.length === 0 ? "all" : value.length === 1 ? value[0] : value.length + " selected"}
      popoverBodyProps={{ p: 0, fontSize: "sm" }}
      popoverContentProps={{ borderRadius: "xl" }}
      isActive={value?.length > 0}
    >
      <InputGroup size="sm" mt="1">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.300" />
        </InputLeftElement>
        <Input
          border="none"
          placeholder="Search industry"
          _active={{ boxShadow: "none" }}
          _focus={{ boxShadow: "none" }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const newValues = [...value]
              if (!value.includes(searchValue)) {
                newValues.push(searchValue)
              }
              setSearchValue("");
              onChange(newValues);
            }
          }}
        />
      </InputGroup>
      <Divider mt="1" mb="2" />
      <VStack
        overflowY="auto"
        maxH="80"
        className="noScrollBar"
        alignItems="flex-start"
        px="4"
        pb="2"
      >
        {filteredIndutries.map((i) => (
          <Box>
            <Checkbox
              isChecked={value.includes(i)}
              onChange={(e) => {
                let newValues = [...value];
                if (e.target.checked) {
                  newValues.push(i);
                } else {
                  newValues = newValues.filter((v) => v !== i);
                }

                onChange(newValues);
              }}
            >
              {i}
            </Checkbox>
          </Box>
        ))}
      </VStack>
      <Divider />
      {value?.length > 0 ? (
        <Wrap p="3" spacing="2">
          {value.map((v) => (
            <WrapItem>
              <Tag size="sm" color="white" bg="#31C52E">
                <HStack>
                  <Text>{v}</Text>
                  <CloseIcon
                    h="2"
                    w="2"
                    cursor="pointer"
                    onClick={() => {
                      onChange([...value].filter((r) => r !== v));
                    }}
                  />
                </HStack>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      ) : (
        <></>
      )}
    </CustomPopover>
  );
}
